import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { AwardIcon, BookOpenIcon, BriefcaseIcon, TrendingUpIcon, ArrowRightIcon, ClockIcon, CheckCircleIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0
  }
};
export function DashboardOverview() {
  return <div className="space-y-6 pb-8" data-id="element-134">
      {/* Profile Banner */}
      <div className="bg-primary-900 rounded-2xl p-6 sm:p-8 relative overflow-hidden" data-id="element-135">
        <div className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/3 w-64 h-64 bg-primary-600 rounded-full blur-3xl opacity-30" data-id="element-136" />
        <div className="relative z-10 flex flex-col sm:flex-row items-center gap-6" data-id="element-137">
          <div className="w-36 h-36 sm:w-44 sm:h-44 rounded-2xl overflow-hidden border-4 border-white/20 shadow-xl flex-shrink-0" data-id="element-138">
            <img src="https://cdn.magicpatterns.com/uploads/126AXCT6wfiSXDSoFCrDVP/ChatGPT_Image_Apr_4,_2026,_05_56_39_PM.png" alt="Marcus Johnson" className="w-full h-full object-cover object-top" data-id="element-139" />
          </div>
          <div className="text-center sm:text-left" data-id="element-140">
            <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight" data-id="element-141">
              Marcus Johnson
            </h1>
            <p className="text-primary-100 text-sm mt-1" data-id="element-142">
              Cloud Computing Track • Cohort 2023
            </p>
            <div className="flex flex-wrap justify-center sm:justify-start gap-2 mt-3" data-id="element-143">
              <span className="bg-white/10 text-primary-100 px-3 py-1 rounded-full text-xs font-medium" data-id="element-144">
                AWS Solutions Architect
              </span>
              <span className="bg-white/10 text-primary-100 px-3 py-1 rounded-full text-xs font-medium" data-id="element-145">
                Azure Certified
              </span>
              <span className="bg-emerald-500/20 text-emerald-300 px-3 py-1 rounded-full text-xs font-medium" data-id="element-146">
                60% Job Ready
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Welcome Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-147">
        <div data-id="element-148">
          <h1 className="text-2xl font-bold text-surface-900 tracking-tight" data-id="element-149">
            Welcome back, Marcus
          </h1>
          <p className="text-surface-500 text-sm mt-1" data-id="element-150">
            Here's what's happening with your career journey today.
          </p>
        </div>
        <Link to="/dashboard/learning" className="inline-flex items-center justify-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors shadow-sm" data-id="element-151">
          Resume Learning
          <ArrowRightIcon className="w-4 h-4" data-id="element-152" />
        </Link>
      </div>

      {/* Stats Grid */}
      <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-153">
        {[{
        label: 'Skills Completed',
        value: '12',
        icon: BookOpenIcon,
        trend: '+2 this week',
        color: 'text-primary-600',
        bg: 'bg-primary-50'
      }, {
        label: 'Certifications',
        value: '3',
        icon: AwardIcon,
        trend: 'Top 15% of cohort',
        color: 'text-primary-700',
        bg: 'bg-primary-100'
      }, {
        label: 'Applications Sent',
        value: '8',
        icon: BriefcaseIcon,
        trend: '2 under review',
        color: 'text-emerald-600',
        bg: 'bg-emerald-50'
      }, {
        label: 'Interview Score',
        value: '85%',
        icon: TrendingUpIcon,
        trend: '+5% improvement',
        color: 'text-amber-600',
        bg: 'bg-amber-50'
      }].map((stat, i) => <motion.div key={i} variants={itemVariants} className="bg-white p-5 rounded-xl border border-surface-200 shadow-sm" data-id="element-154">
            <div className="flex items-center justify-between mb-4" data-id="element-155">
              <div className={`w-10 h-10 rounded-lg ${stat.bg} flex items-center justify-center`} data-id="element-156">
                <stat.icon className={`w-5 h-5 ${stat.color}`} data-id="element-157" />
              </div>
            </div>
            <div data-id="element-158">
              <h3 className="text-3xl font-bold text-surface-900 tracking-tight" data-id="element-159">
                {stat.value}
              </h3>
              <p className="text-sm font-medium text-surface-600 mt-1" data-id="element-160">
                {stat.label}
              </p>
              <p className="text-xs text-surface-400 mt-2" data-id="element-161">{stat.trend}</p>
            </div>
          </motion.div>)}
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-162">
        {/* Main Content Column */}
        <div className="lg:col-span-2 space-y-6" data-id="element-163">
          {/* Current Path Progress */}
          <motion.div variants={itemVariants} className="bg-white p-6 rounded-xl border border-surface-200 shadow-sm" data-id="element-164">
            <div className="flex items-center justify-between mb-6" data-id="element-165">
              <h2 className="text-lg font-semibold text-surface-900" data-id="element-166">
                Current Learning Path
              </h2>
              <span className="text-sm font-medium text-primary-600 bg-primary-50 px-2.5 py-1 rounded-md" data-id="element-167">
                Cloud Computing (AWS)
              </span>
            </div>

            <div className="space-y-4" data-id="element-168">
              <div data-id="element-169">
                <div className="flex justify-between text-sm mb-2" data-id="element-170">
                  <span className="font-medium text-surface-700" data-id="element-171">
                    AWS Solutions Architect Associate
                  </span>
                  <span className="text-surface-500" data-id="element-172">65% Completed</span>
                </div>
                <div className="w-full bg-surface-100 rounded-full h-2.5 overflow-hidden" data-id="element-173">
                  <div className="bg-primary-600 h-2.5 rounded-full" style={{
                  width: '65%'
                }} data-id="element-174"></div>
                </div>
              </div>

              <div className="pt-4 border-t border-surface-100" data-id="element-175">
                <h3 className="text-sm font-medium text-surface-900 mb-3" data-id="element-176">
                  Up Next
                </h3>
                <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-surface-50 transition-colors border border-transparent hover:border-surface-200 cursor-pointer" data-id="element-177">
                  <div className="mt-0.5" data-id="element-178">
                    <div className="w-8 h-8 rounded-full bg-surface-100 flex items-center justify-center" data-id="element-179">
                      <ClockIcon className="w-4 h-4 text-surface-500" data-id="element-180" />
                    </div>
                  </div>
                  <div data-id="element-181">
                    <p className="text-sm font-medium text-surface-900" data-id="element-182">
                      Module 4: VPCs and Networking
                    </p>
                    <p className="text-xs text-surface-500 mt-1" data-id="element-183">
                      Est. time: 45 mins • Video & Lab
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Recent Job Matches */}
          <motion.div variants={itemVariants} className="bg-white p-6 rounded-xl border border-surface-200 shadow-sm" data-id="element-184">
            <div className="flex items-center justify-between mb-6" data-id="element-185">
              <h2 className="text-lg font-semibold text-surface-900" data-id="element-186">
                Recommended Jobs
              </h2>
              <Link to="/dashboard/jobs" className="text-sm font-medium text-primary-600 hover:text-primary-700" data-id="element-187">
                View all
              </Link>
            </div>

            <div className="space-y-3" data-id="element-188">
              {[{
              role: 'Junior Cloud Engineer',
              company: 'TechFlow Solutions',
              match: '92%',
              location: 'Remote'
            }, {
              role: 'IT Support Specialist',
              company: 'HealthSync',
              match: '88%',
              location: 'Chicago, IL'
            }, {
              role: 'AWS Operations Associate',
              company: 'CloudScale',
              match: '85%',
              location: 'Remote'
            }].map((job, i) => <div key={i} className="flex items-center justify-between p-4 rounded-lg border border-surface-100 hover:border-primary-200 hover:shadow-sm transition-all group cursor-pointer" data-id="element-189">
                  <div className="flex items-center gap-4" data-id="element-190">
                    <div className="w-10 h-10 rounded-lg bg-surface-100 flex items-center justify-center font-bold text-surface-600" data-id="element-191">
                      {job.company.charAt(0)}
                    </div>
                    <div data-id="element-192">
                      <h3 className="text-sm font-semibold text-surface-900 group-hover:text-primary-600 transition-colors" data-id="element-193">
                        {job.role}
                      </h3>
                      <p className="text-xs text-surface-500 mt-0.5" data-id="element-194">
                        {job.company} • {job.location}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2" data-id="element-195">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100" data-id="element-196">
                      {job.match} Match
                    </span>
                  </div>
                </div>)}
            </div>
          </motion.div>
        </div>

        {/* Sidebar Column */}
        <div className="space-y-6" data-id="element-197">
          {/* Upcoming Events */}
          <motion.div variants={itemVariants} className="bg-white p-6 rounded-xl border border-surface-200 shadow-sm" data-id="element-198">
            <h2 className="text-lg font-semibold text-surface-900 mb-4" data-id="element-199">
              Upcoming Events
            </h2>
            <div className="space-y-4" data-id="element-200">
              {[{
              title: 'Mock Interview Session',
              date: 'Today, 2:00 PM',
              type: 'Mentorship'
            }, {
              title: 'Resume Workshop',
              date: 'Tomorrow, 10:00 AM',
              type: 'Workshop'
            }, {
              title: 'TechFlow Info Session',
              date: 'Thu, 4:00 PM',
              type: 'Employer'
            }].map((event, i) => <div key={i} className="flex gap-3" data-id="element-201">
                  <div className="flex flex-col items-center min-w-[40px]" data-id="element-202">
                    <div className="w-2 h-2 rounded-full bg-primary-500 mt-1.5" data-id="element-203" />
                    {i !== 2 && <div className="w-px h-full bg-surface-200 mt-2" data-id="element-204" />}
                  </div>
                  <div className="pb-4" data-id="element-205">
                    <p className="text-sm font-medium text-surface-900" data-id="element-206">
                      {event.title}
                    </p>
                    <p className="text-xs text-surface-500 mt-0.5" data-id="element-207">
                      {event.date}
                    </p>
                    <span className="inline-block mt-1.5 px-2 py-0.5 bg-surface-100 text-surface-600 text-[10px] uppercase font-semibold rounded" data-id="element-208">
                      {event.type}
                    </span>
                  </div>
                </div>)}
            </div>
          </motion.div>

          {/* Quick Actions */}
          <motion.div variants={itemVariants} className="bg-white p-6 rounded-xl border border-surface-200 shadow-sm" data-id="element-209">
            <h2 className="text-lg font-semibold text-surface-900 mb-4" data-id="element-210">
              Quick Actions
            </h2>
            <div className="space-y-2" data-id="element-211">
              <button className="w-full flex items-center justify-between p-3 rounded-lg border border-surface-200 hover:bg-surface-50 hover:border-surface-300 transition-colors text-left" data-id="element-212">
                <span className="text-sm font-medium text-surface-700" data-id="element-213">
                  Update Resume
                </span>
                <ArrowRightIcon className="w-4 h-4 text-surface-400" data-id="element-214" />
              </button>
              <button className="w-full flex items-center justify-between p-3 rounded-lg border border-surface-200 hover:bg-surface-50 hover:border-surface-300 transition-colors text-left" data-id="element-215">
                <span className="text-sm font-medium text-surface-700" data-id="element-216">
                  Message Mentor
                </span>
                <ArrowRightIcon className="w-4 h-4 text-surface-400" data-id="element-217" />
              </button>
              <button className="w-full flex items-center justify-between p-3 rounded-lg border border-surface-200 hover:bg-surface-50 hover:border-surface-300 transition-colors text-left" data-id="element-218">
                <span className="text-sm font-medium text-surface-700" data-id="element-219">
                  Browse New Courses
                </span>
                <ArrowRightIcon className="w-4 h-4 text-surface-400" data-id="element-220" />
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>;
}